/**
 * Progressive enhancement for the localization overlay (`sections/localization-overlay.liquid`).
 *
 * Everything works as a plain `{% form 'localization' %}` POST without this module: native radios
 * for country and language and a submit button, or, with the languages alone, one submit button
 * per language. This only adds what native HTML can't do:
 *
 *  - Reveals the country search field (rendered `hidden` so it never sits dead without JS) and
 *    filters the row list as the shopper types, purely by hiding non-matching `<li>` rows. The
 *    list stays one real radio group throughout; nothing is removed or re-ordered. While a query
 *    is active the "Suggested" / "All Countries and Regions" group headers hide too, so the
 *    filtered result reads as a flat list.
 *  - Announces the visible country count in a status live region.
 *  - Marks the form busy while it submits, so the pending state is announced before the page
 *    navigates away.
 *  - Restores focus to whichever control opened the popover when it closes. The Popover API
 *    already does this natively, but it silently fails whenever the opening control is inside
 *    another popover (the mobile menu drawer's own localization row) or, for the plain desktop
 *    trigger, in WebKit alone: focus falls back to `<body>` instead, which drops keyboard and
 *    screen-reader users with no context. This only steps in when that native restoration
 *    didn't happen.
 */
const popover = document.getElementById("localization");
let invoker = null;

// Whether the shopper's last input was a key. Safari shows every focus a script sets as keyboard
// focus: after a tap that closed the overlay, the button the focus returned to stood in a focus
// ring on an iPhone. `focusVisible` says which it is (Safari and Firefox read it; Chromium decides
// the same way by itself).
let lastInputWasKey = false;
document.addEventListener("keydown", () => (lastInputWasKey = true), true);
document.addEventListener("pointerdown", () => (lastInputWasKey = false), true);

// Track the control that opened the popover by the click/activation itself, not by
// `document.activeElement`: Safari doesn't focus a `<button>` on mouse click at all, so the
// value the Popover API records as "previously focused element" is `<body>` there from the
// very start, even before the popover ever opens.
document.addEventListener(
  "click",
  (event) => {
    // Only a click that is about to open the popover counts as its invoker: the close button
    // inside it also carries `popovertarget="localization"` (to hide it), and would otherwise
    // overwrite the real invoker with itself right as it becomes unreachable.
    if (popover?.matches(":popover-open")) return;
    const trigger = event.target.closest?.('[popovertarget="localization"]');
    if (trigger) invoker = trigger;
  },
  true,
);

function restoreFocus(target) {
  if (!target) return;
  const active = document.activeElement;
  // Native restoration didn't happen if focus is still sitting on the (now hidden, no longer
  // visible) element the popover contained, or already fell back to <body>/<html>.
  const lost =
    !active ||
    active === document.body ||
    active === document.documentElement ||
    !active.isConnected ||
    active.checkVisibility?.() === false;
  if (lost && target.isConnected && target.checkVisibility?.()) target.focus({ focusVisible: lastInputWasKey });
}

popover?.addEventListener("toggle", (event) => {
  if (event.newState !== "closed") return;
  const target = invoker;
  invoker = null;
  // Whether the outgoing focus target already reports as hidden at this point is inconsistent
  // across engines: some apply it immediately, others only once the exit transition (see the
  // `[popover]` rules in theme.css, `--duration-out`) finishes and `display: none` actually
  // takes effect. Check right away for the engines that already show it, and again once the
  // transition has had time to finish for the ones that don't.
  restoreFocus(target);
  setTimeout(() => restoreFocus(target), 300);
});
// Back from the page in the new language (bfcache): the sheet would return open, the clicked
// language still looking selected next to the current one.
window.addEventListener("pageshow", (event) => {
  if (!event.persisted || !popover) return;
  for (const busy of popover.querySelectorAll("[aria-busy]")) busy.removeAttribute("aria-busy");
  if (popover.matches(":popover-open")) popover.hidePopover();
});

class LocalizationSearch extends HTMLElement {
  #input = null;
  #clear = null;
  #status = null;
  #empty = null;
  #groups = [];
  #rows = [];
  #form = null;

  connectedCallback() {
    this.#form = this.closest("form");
    this.#form?.addEventListener("submit", this.#onSubmit);

    this.#input = this.querySelector("[data-localization-query]");
    this.#clear = this.querySelector("[data-localization-clear]");
    this.#status = this.querySelector("[data-localization-status]");
    this.#empty = this.querySelector("[data-localization-empty]");
    this.#groups = [...this.querySelectorAll("[data-localization-group]")];
    this.#rows = [...this.querySelectorAll("[data-localization-row]")];
    if (!this.#input || this.#rows.length === 0) return;

    this.querySelector("[data-localization-search]")?.removeAttribute("hidden");
    this.#input.addEventListener("input", this.#onInput);
    this.#input.addEventListener("keydown", this.#onKey);
    this.#clear?.addEventListener("click", this.#onClear);
  }

  disconnectedCallback() {
    this.#form?.removeEventListener("submit", this.#onSubmit);
    this.#input?.removeEventListener("input", this.#onInput);
    this.#input?.removeEventListener("keydown", this.#onKey);
    this.#clear?.removeEventListener("click", this.#onClear);
  }

  // The field filters the list as it is typed in: Return in it must not send the form it stands
  // in (it saved the selection and reloaded the page). On a touch screen it puts the on-screen
  // keyboard away instead, except the Return that ends an input method's composition (Safari:
  // Enter with the key code 229).
  #onKey = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (event.isComposing || event.keyCode === 229) return;
    if (window.matchMedia("(pointer: coarse)").matches) this.#input.blur();
  };

  #onInput = () => {
    const query = this.#input.value.trim().toLocaleLowerCase();
    if (this.#clear) this.#clear.hidden = query === "";

    let visible = 0;
    for (const row of this.#rows) {
      const match = query === "" || row.dataset.localizationRow.includes(query);
      row.closest("li").hidden = !match;
      if (match) visible += 1;
    }
    for (const group of this.#groups) {
      const hasVisibleRows = !!group.querySelector("li:not([hidden])");
      group.hidden = !hasVisibleRows;
      const title = group.querySelector(".group-title");
      if (title) title.hidden = query !== "";
    }
    if (this.#empty) {
      this.#empty.hidden = visible !== 0;
      if (visible === 0) {
        this.#empty.textContent = (this.#empty.dataset.template || "").replace("__QUERY__", this.#input.value.trim());
      }
    }
    this.#announce(visible);
  };

  #onClear = () => {
    this.#input.value = "";
    this.#input.focus();
    this.#onInput();
  };

  #announce(count) {
    if (!this.#status) return;
    const template = count === 1 ? this.dataset.statusOne : (this.dataset.statusOther || "").replace("N", count);
    this.#status.textContent = template || "";
  }

  #onSubmit = (event) => {
    this.#form?.setAttribute("aria-busy", "true");
    // A language button submits by itself: it takes the selected look at once, so the click
    // shows before the page in the new language arrives.
    if (event.submitter?.matches(".lang-option")) event.submitter.setAttribute("aria-busy", "true");
    if (this.#status) this.#status.textContent = this.dataset.statusUpdating || "";
  };
}

customElements.define("localization-search", LocalizationSearch);
